import { ChevronRight } from '@mui/icons-material'
import { Button, FormControl, IconButton, InputAdornment, InputLabel, MenuItem, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import Loading from 'components/loading'
import { IContractDTO } from 'data/dtos/automation/i-contract-dto'
import { useHistory } from 'react-router-dom'
import { loadList } from 'services/requests'
import useContract from 'store/contracts'
import SearchIcon from '@mui/icons-material/Search';
import { useState } from 'react'

const ContractsTable = () => {
    const { actualOrganization } = useContract()
    const history = useHistory()
    const [currentFilters, setCurrentFilters] = useState({
        contractName: '',
        operatorType: 'TODOS',
        productType: 'TODOS',
        situationType: 'TODOS',
    });

    const { data, isLoading, isFetching } = useQuery({
        queryKey: ['list', 'contracts', 'organization', actualOrganization.id],
        queryFn: () =>
            loadList('/contracts/list-contract', {
                search: '',
                page: 0,
                rowsPerPage: 20,
                columnOrder: [],
                organizationId: actualOrganization.id,
            }),
        select: (data) => data.data.data as IContractDTO[],
        enabled: !!actualOrganization.id,
    })

    function handleChangeFilters(filters: typeof currentFilters) {
        setCurrentFilters({ ...filters });
    };

    function handleFilterLoad (contracts: IContractDTO[]): IContractDTO[] {
        return contracts.filter(contract => {
            const filterName = contract?.name?.toLowerCase()?.includes(currentFilters?.contractName?.toLocaleLowerCase()) || contract?.buyer?.corporateName?.toLocaleLowerCase()?.includes(currentFilters?.contractName?.toLocaleLowerCase());
            const filterSituationType = currentFilters?.situationType === 'TODOS' || (!!contract?.isInactive && currentFilters?.situationType === 'Inativo') || (!contract?.isInactive && currentFilters?.situationType === 'Ativo')
            const filterOperatorType = currentFilters?.operatorType === 'TODOS' || contract?.supplier?.corporateName === currentFilters?.operatorType;
            const filterProductType = currentFilters?.productType === 'TODOS' || contract?.name === currentFilters?.productType;

            return filterName && filterSituationType && filterOperatorType && filterProductType;
        })
    }

    if (isLoading || isFetching || data == undefined) {
        return <Loading />
    }

    return (
        <>
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '10px' }}>
                <TextField
                    fullWidth
                    label='Nome do contrato ou nome da empresa principal'
                    placeholder='Digite para pesquisar'
                    variant='outlined'
                    value={currentFilters?.contractName}
                    onChange={(e) => handleChangeFilters({ ...currentFilters, contractName: e.target.value })}
                    InputProps={{ 
                        startAdornment: (
                            <InputAdornment position='start'>
                                <SearchIcon color='action' style={{ width: '24px', aspectRatio: '1' }}/>
                            </InputAdornment>
                        ),
                    }}
                />
                <div style={{ width: '100%', display: 'flex', flexDirection: 'row', gap: '8px', alignItems: 'center', marginBottom: '24px' }}>
                    <Typography color='gray' variant='body2'>Filtrar por</Typography>
                    <FormControl style={{ width: '200px' }}>
                        <InputLabel id="operadora">Operadora</InputLabel>
                        <Select
                            labelId="operadora"
                            value={currentFilters?.operatorType}
                            onChange={(e) => handleChangeFilters({ ...currentFilters, operatorType: e.target.value })}
                            style={{ height: '55.3px', marginBottom: '2.7px' }}
                        >
                            {data.map(contract => contract?.supplier?.corporateName)?.reduce((acc, supplierName) => {
                                if (!acc?.includes(supplierName)) acc?.push(supplierName)
                                return acc
                            }, [] as string[])?.map(contract => (
                                <MenuItem key={contract} value={contract}>{contract}</MenuItem>
                            ))}
                            <MenuItem value='TODOS'>Todos</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl style={{ width: '200px' }}>
                        <InputLabel id="produto">Produto</InputLabel>
                        <Select
                            labelId="produto"
                            value={currentFilters?.productType}
                            onChange={(e) => handleChangeFilters({ ...currentFilters, productType: e.target.value })}
                            style={{ height: '55.3px', marginBottom: '2.7px' }}
                        >
                            {data.map(contract => contract?.name)?.reduce((acc, contractName) => {
                                if (!acc?.includes(contractName)) acc?.push(contractName)
                                return acc
                            }, [] as string[])?.map(contract => (
                                <MenuItem key={contract} value={contract}>{contract}</MenuItem>
                            ))}
                            <MenuItem value='TODOS'>Todos</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl style={{ width: '200px' }}>
                        <InputLabel id="situation">Situação</InputLabel>
                        <Select
                            labelId="situation"
                            value={currentFilters?.situationType}
                            onChange={(e) => handleChangeFilters({ ...currentFilters, situationType: e.target.value })}
                            style={{ height: '55.3px', marginBottom: '2.7px' }}
                        >
                            <MenuItem value='Inativo'>Inativo</MenuItem>
                            <MenuItem value='Ativo'>Ativo</MenuItem>
                            <MenuItem value='TODOS'>Todos</MenuItem>
                        </Select>
                    </FormControl>
                    <Button 
                        onClick={() => handleChangeFilters({ contractName: '', operatorType: 'TODOS', productType: 'TODOS', situationType: 'TODOS' })}
                    >
                        Limpar filtro
                    </Button>
                </div>
            </div>
            <TableContainer
                className='!max-h-[27rem] !h-full !overflow-y-auto'
                sx={{
                    '& .MuiTableCell-root': {
                        background: '#FFFFFF !important',
                    },
                }}
            >
                <Table stickyHeader>
                    <TableHead>
                        <TableRow style={{ height: '70px'}}>
                            <TableCell>Id</TableCell>
                            <TableCell>Operadora</TableCell>
                            <TableCell>Produto</TableCell>
                            <TableCell>Empresa principal</TableCell>
                            <TableCell>Situação</TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {handleFilterLoad(data).map((row) => (
                            <TableRow
                                key={row?.id}
                                hover
                                style={{ cursor: 'pointer', height: '70px' }}
                                onClick={() => {
                                    history.push(`/contract/${actualOrganization?.id}/${row?.id}`)
                                }}>
                                <TableCell>{row?.id?.split("-")?.[0]?.toUpperCase()}</TableCell>
                                <TableCell>{row?.supplier?.corporateName}</TableCell>
                                <TableCell>{row?.name}</TableCell>
                                <TableCell>{row?.buyer?.corporateName}</TableCell>
                                <TableCell>{row?.isInactive ? (
                                    <div style={{ color: '#191C1A', backgroundColor: '#191C1A14', opacity: '0.8', padding: '0.25rem 0.75rem', borderRadius: '0.75rem', fontWeight: '400', fontSize: '12px', lineHeight: '16px', width: 'fit-content', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                        <span>Inativo</span>
                                    </div>
                                ) : (
                                    <div style={{ color: '#2E7D32', backgroundColor: '#2E7D3214', opacity: '0.8', padding: '0.25rem 0.75rem', borderRadius: '0.75rem', fontWeight: '400', fontSize: '12px', lineHeight: '16px', width: 'fit-content', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                        <span>Ativo</span>
                                    </div>
                                )}</TableCell>
                                <TableCell>
                                    <IconButton size="small">
                                        <ChevronRight />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    )
}

export default ContractsTable
